/**
 * The policy pages: terms, privacy, refunds.
 *
 * One narrow column of prose, the same shell as the landing page so the
 * language switch and the wordmark stay where a visitor left them. Card
 * acquirers require these to exist and to be reachable; a reader arriving from
 * an invoice should find them readable too.
 */

import { h } from '../lib/dom.js';
import { LANGS, LANG_LABEL } from '../content.js';
import { BUSINESS, LEGAL, PAGES } from '../legal.js';
import { pathFor } from '../lib/router.js';

const wordmark = '/assets/veni-wordmark.svg';

export function renderLegal(lang, page, { onLangChange, onNavigate }) {
  const l = LEGAL[lang];
  const doc = l[page];

  return h(
    'div',
    { class: 'page', dataset: { lang } },
    h('a', { class: 'skip-link', href: '#main' }, 'Skip to content'),
    nav(lang, l, { onLangChange, onNavigate }),
    h(
      'main',
      { class: 'shell legal', id: 'main' },
      h('h1', { class: 'legal-title' }, doc.title),
      h('p', { class: 'legal-updated' }, `${l.updated} ${l.updatedDate}`),
      ...doc.sections.map(([heading, paragraphs]) =>
        h(
          'section',
          { class: 'legal-section' },
          h('h2', null, heading),
          ...paragraphs.map((text) => h('p', null, text)),
        ),
      ),
      details(l),
      h('hr', { class: 'hr', style: 'margin: 40px 0 0' }),
      // These pages carry no site footer, so the other two policies have to
      // be reachable from here — a reader who lands on one from an invoice
      // should not have to go home to find the next.
      h(
        'p',
        { class: 'legal-back' },
        link(lang, null, l.back, onNavigate),
        ...PAGES.filter((other) => other !== page).flatMap((other) => [
          ' · ',
          link(lang, other, l.nav[other], onNavigate),
        ]),
      ),
    ),
  );
}

function link(lang, page, label, onNavigate) {
  return h(
    'a',
    {
      href: pathFor(lang, page),
      onClick: (e) => { e.preventDefault(); onNavigate(lang, page); },
    },
    label,
  );
}

/**
 * The registered name, number and address, on every policy page rather than
 * only one — an acquirer checking the site should not have to hunt for them,
 * and nor should a client.
 */
function details(l) {
  return h(
    'section',
    { class: 'legal-section legal-details' },
    h('h2', null, l.details.title),
    h(
      'dl',
      { class: 'legal-dl' },
      ...l.details.rows.flatMap(([label, value]) => [
        h('dt', null, label),
        h('dd', null, value),
      ]),
    ),
  );
}

function nav(lang, l, { onLangChange, onNavigate }) {
  return h(
    'nav',
    { class: 'nav' },
    h(
      'a',
      {
        class: 'nav-brand',
        href: pathFor(lang),
        onClick: (e) => { e.preventDefault(); onNavigate(lang, null); },
      },
      h('img', { src: wordmark, alt: BUSINESS.name, width: 120, height: 22 }),
    ),
    h(
      'span',
      { class: 'lang-switch', role: 'group', 'aria-label': 'Language' },
      LANGS.map((code) =>
        h(
          'button',
          {
            type: 'button',
            class: 'lang-btn',
            lang: code,
            dataset: { lang: code },
            'aria-current': code === lang ? 'true' : 'false',
            onClick: () => onLangChange(code),
          },
          LANG_LABEL[code],
        ),
      ),
    ),
  );
}
